import { useEffect } from 'react';

/**
 * Detects when a NEW admin build has been deployed while this tab stayed open, and invites the user
 * to reload — so they never have to hard-refresh to escape a stale bundle. index.html is served
 * `no-cache`, so re-fetching it always reflects the live deploy; we compare the entry-chunk hash
 * it references to the one THIS tab booted with. Checks on window focus and every few minutes.
 */
function currentEntrySrc(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  return el?.src ?? null;
}

async function deployedEntrySrc(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/);
    return m ? new URL(m[0], location.origin).href : null;
  } catch {
    return null;
  }
}

export function useVersionWatch(onNewVersion: () => void): void {
  useEffect(() => {
    const boot = currentEntrySrc();
    if (!boot) return; // dev server / no hashed entry — nothing to compare
    let done = false;

    const check = async () => {
      if (done || document.visibilityState !== 'visible') return;
      const latest = await deployedEntrySrc();
      if (latest && latest !== boot) {
        done = true;
        onNewVersion();
      }
    };

    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const timer = window.setInterval(() => void check(), 5 * 60_000);
    void check();

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(timer);
    };
  }, [onNewVersion]);
}
